import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Add,
  Search,
  FilterList
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import ProjectCard from '../components/Dashboard/ProjectCard';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import CreateProjectModal from '../components/Common/CreateProjectModal';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { Project, ProjectMetrics, CreateProjectData, UserRole } from '../types';
import { useNavigate } from 'react-router-dom';

const Projects: React.FC = () => {
  const { user, selectedOrganizationId } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectMetrics, setProjectMetrics] = useState<Record<string, ProjectMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [projectCreateStatus, setProjectCreateStatus] = useState<string>('');

  // Redirect ROOT_USER to root admin dashboard
  useEffect(() => {
    if (user?.role === UserRole.ROOT_USER) {
      navigate('/root-admin-dashboard', { replace: true });
    }
  }, [user?.role, navigate]);

  useEffect(() => {
    loadProjects();
  }, [selectedOrganizationId]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use getUserProjects for regular users, getProjects for admin users
      const isAdmin = user?.role && ['admin', 'super_manager'].includes(user.role);
      const isRootUser = user?.role === 'root_user';
      
      // ROOT_USER MUST select organization first - PRIVACY PROTECTION
      if (isRootUser && !selectedOrganizationId) {
        console.warn('🔒 ROOT_USER must select organization first - blocking data load for privacy');
        setProjects([]);
        setLoading(false);
        return; // BLOCK data loading until org selected
      }
      
      let projectsData;
      if (isRootUser) {
        // Root user with selected organization
        projectsData = await apiService.getProjects(selectedOrganizationId!);
      } else if (isAdmin) {
        projectsData = await apiService.getProjects();
      } else {
        projectsData = await apiService.getUserProjects();
      }
      
      setProjects(projectsData);
      
      // Load metrics for each project (only for admin users)
      if (isAdmin) {
        const metricsPromises = projectsData.map(async (project) => {
          try {
            const metrics = await apiService.getProjectMetrics(project.id);
            return { projectId: project.id, metrics };
          } catch (error) {
            console.error(`Error loading metrics for project ${project.id}:`, error);
            return { projectId: project.id, metrics: null };
          }
        });
        
        const metricsResults = await Promise.all(metricsPromises);
        const metricsMap = metricsResults.reduce((acc, result) => {
          if (result.metrics) {
            acc[result.projectId] = result.metrics;
          }
          return acc;
        }, {} as Record<string, ProjectMetrics>);
        
        setProjectMetrics(metricsMap);
      } else {
        // For non-admin users, set empty metrics
        setProjectMetrics({});
      }
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProjectSuccess = async (project: any) => {
    setProjectCreateStatus('Project created successfully!');
    setTimeout(() => setProjectCreateStatus(''), 3000);
    
    // Reload projects to show the new one
    await loadProjects();
  };

  const handleCreateProjectError = (error: string) => {
    setProjectCreateStatus(`Error: ${error}`);
    setTimeout(() => setProjectCreateStatus(''), 5000);
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (project.description && project.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const canCreateProject = () => {
    return user?.role && ['root_user', 'admin', 'hr_admin', 'super_manager'].includes(user.role);
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner message="Loading projects..." />
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Modern Professional Header */}
      <Box sx={{ 
        mb: 3,
        background: (theme) => theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: (theme) => theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        p: 3,
        borderRadius: 2
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" fontWeight="bold">
            Projects
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => window.location.href = '/boards'}
            >
              View All Boards
            </Button>
            {canCreateProject() && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setCreateProjectModalOpen(true)}
            >
              Create Project
            </Button>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" color="text.secondary">
          Manage and track progress across all your projects
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search projects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 300 }}
        />
        
        <Button
          variant="outlined"
          startIcon={<FilterList />}
        >
          Filters
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'No projects match your search' : 'No projects found'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {searchTerm ? 'Try adjusting your search terms' : 'Create your first project to get started'}
          </Typography>
          {canCreateProject() && !searchTerm && (
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setCreateProjectModalOpen(true)}
              sx={{ mt: 2 }}
            >
              Create Project
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredProjects.map((project) => (
            <Grid item xs={12} md={6} lg={4} key={project.id}>
              <ProjectCard
                project={project}
                metrics={projectMetrics[project.id]}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button for Mobile */}
      {canCreateProject() && (
        <Fab
          color="primary"
          aria-label="create project"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            display: { xs: 'flex', md: 'none' }
          }}
          onClick={() => setCreateProjectModalOpen(true)}
        >
          <Add />
        </Fab>
      )}

      {/* Create Project Modal */}
      <CreateProjectModal
        open={createProjectModalOpen}
        onClose={() => setCreateProjectModalOpen(false)}
        onSuccess={handleCreateProjectSuccess}
        onError={handleCreateProjectError}
        useAdminTasksAPI={true}
      />

      {/* Project Creation Status Alert */}
      {projectCreateStatus && (
        <Alert 
          severity={projectCreateStatus.includes('Error') ? 'error' : 'success'}
          sx={{ 
            position: 'fixed', 
            top: 80, 
            right: 20, 
            zIndex: 9999,
            minWidth: 300,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {projectCreateStatus}
        </Alert>
      )}
    </Layout>
  );
};

export default Projects;